import { conns, connsPublic } from '@cfg/axiosConfig.tsx';

import type {
  QuoteCreatePayload,
  QuoteNotificationOut,
  QuoteOut,
  QuotePage,
  QuotePosDraftOut,
  QuotePublicCreateOut,
  QuotePublicOut,
  QuoteSource,
  QuoteStatsOut,
  QuoteSortField,
  QuoteSortOrder,
  QuoteStatus,
  QuoteUpdatePayload,
} from '../types';

const QUOTES_KEY = 'erp:quotes:list';
const PUBLIC_QUOTES_KEY = 'eshop:quotes:public';

export interface FetchQuotesParams {
  page?: number;
  page_size?: number;
  status?: QuoteStatus[];
  search?: string;
  customer_id?: number;
  assigned_staff_id?: number;
  created_from?: string;
  created_to?: string;
  expires_from?: string;
  expires_to?: string;
  source?: QuoteSource[];
  total_from?: number;
  total_to?: number;
  sort_by?: QuoteSortField;
  sort_order?: QuoteSortOrder;
}

export const fetchQuotes = async (params: FetchQuotesParams = {}): Promise<QuotePage> => {
  const { data } = await conns<QuotePage>(QUOTES_KEY).get(params);
  return data;
};

export const fetchQuoteStats = async (params: FetchQuotesParams = {}): Promise<QuoteStatsOut> => {
  const { data } = await conns<QuoteStatsOut>('erp:quotes:stats').get(params);
  return data;
};

export const fetchQuote = async (quoteId: number): Promise<QuoteOut> => {
  const { data } = await conns<QuoteOut>('erp:quotes:detail', [quoteId]).get();
  return data;
};

export const createQuote = async (payload: QuoteCreatePayload): Promise<QuoteOut> => {
  const { data } = await conns<QuoteOut>('erp:quotes:create').post(payload);
  return data;
};

export const updateQuote = async (quoteId: number, payload: QuoteUpdatePayload): Promise<QuoteOut> => {
  const { data } = await conns<QuoteOut>('erp:quotes:detail', [quoteId]).patch(payload);
  return data;
};

export const cancelQuote = async (quoteId: number, reason?: string): Promise<QuoteOut> => {
  const { data } = await conns<QuoteOut>('erp:quotes:cancel', [quoteId, 'cancel']).post({ reason: reason ?? null });
  return data;
};

export const createQuotePublicLink = async (quoteId: number): Promise<QuotePublicCreateOut> => {
  const { data } = await conns<QuotePublicCreateOut>('erp:quotes:public_link', [quoteId, 'public-link']).post();
  return data;
};

export const notifyQuote = async (
  quoteId: number,
  channel: 'email' | 'whatsapp' | 'manual',
): Promise<QuoteNotificationOut[]> => {
  const { data } = await conns<QuoteNotificationOut[]>('erp:quotes:notify', [quoteId, 'notify']).post({ channel });
  return data;
};

export const getQuotePdfUrl = (quoteId: number): string =>
  `/api/v1/erp/quotes/${quoteId}/pdf`;

export const fetchQuotePosDraft = async (quoteId: number): Promise<QuotePosDraftOut> => {
  const { data } = await conns<QuotePosDraftOut>('erp:quotes:pos_draft', [quoteId, 'pos-draft']).get();
  return data;
};

export const markQuoteOpenedInPos = async (quoteId: number): Promise<QuoteOut> => {
  const { data } = await conns<QuoteOut>('erp:quotes:opened_in_pos', [quoteId, 'opened-in-pos']).post();
  return data;
};

export const markQuoteConverted = async (quoteId: number, orderId: number): Promise<QuoteOut> => {
  const { data } = await conns<QuoteOut>('erp:quotes:mark_converted', [quoteId, 'mark-converted']).post({
    order_id: orderId,
  });
  return data;
};

export const fetchPublicQuote = async (publicId: string, token: string): Promise<QuotePublicOut> => {
  const { data } = await connsPublic<QuotePublicOut>(PUBLIC_QUOTES_KEY, [publicId]).get({ token });
  return data;
};

export const confirmPublicQuote = async (publicId: string, token: string): Promise<QuotePublicOut> => {
  const { data } = await connsPublic<QuotePublicOut>('eshop:quotes:confirm', [publicId, 'confirm']).post(undefined, {
    params: { token },
  });
  return data;
};
